/**
 * Centralized error normalization for the API layer.
 *
 * Goal: every caller can pull a user-facing string out of any
 * thrown error with `extractMessage(err, fallback)` — no more
 * `e.response?.data?.detail || e.message || 'Something failed'`
 * repeated at 462 callsites.
 *
 * The shape of the thrown error is preserved (Axios error w/
 * `.response`, `.request`, `.config`) so existing callers that
 * read `err.response?.status` keep working unchanged.
 */

/**
 * Pull the most useful user-facing message out of an Axios error.
 *
 * Order of preference:
 *   1. response.data.detail       (FastAPI default error key)
 *   2. response.data.message      (custom error key some endpoints use)
 *   3. response.statusText        (HTTP status, e.g. "Not Found")
 *   4. err.message                (network/timeout text)
 *   5. fallback                   (caller-supplied default)
 */
export function extractMessage(err, fallback = 'Request failed') {
  if (!err) return fallback;
  const data = err.response?.data;
  if (data && typeof data === 'object') {
    if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;
    if (typeof data.message === 'string' && data.message.trim()) return data.message;
  }
  if (err.response?.statusText) return err.response.statusText;
  if (typeof err.message === 'string' && err.message.trim()) return err.message;
  return fallback;
}

/**
 * Convenience boolean — true for 4xx/5xx HTTP responses.
 * Network/timeout failures return false (use `extractMessage` for
 * the text in that case).
 */
export function isHttpError(err) {
  return !!err?.response?.status;
}
